import React from 'react';
import { Link, Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';
import SEO from '../../shared/components/SEO';

// Stable anchor id from a section title, so every clause is linkable and a
// reviewer can cite "re-stack.com/terms#limitation-of-liability" rather than
// "section 12".
const slugify = (title) =>
  title
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const sections = [
  {
    title: 'Acceptance of These Terms',
    paragraphs: [
      'These Terms of Service govern your access to and use of the Restack website, Microsoft Office add-ins, and related services. By accessing or using Restack, you agree to be bound by these Terms.',
      'If you are using Restack on behalf of a company, firm, or other organization, you represent that you are authorized to bind that entity to these Terms.',
    ],
  },
  {
    title: 'The Service',
    paragraphs: [
      'Restack provides AI-assisted tools for researching, drafting, editing, analyzing, and formatting content in Microsoft PowerPoint, Excel, and Word. Features may include prompt-based generation, Office content editing, chart creation, exports, integrations, collaboration features, and billing-managed access to premium functionality.',
      'We may change, improve, suspend, or discontinue parts of the service at any time.',
    ],
  },
  {
    title: 'Eligibility and Accounts',
    bullets: [
      'You must provide accurate account information and keep it up to date.',
      'You are responsible for maintaining the confidentiality of your account credentials and for activities that occur under your account.',
      'You must promptly notify us if you believe your account has been compromised or used without authorization.',
    ],
  },
  {
    title: 'Subscriptions, Billing, and Purchases',
    paragraphs: [
      'Some parts of Restack require a paid subscription or paid usage credits. Pricing, plan limits, renewal terms, and cancellation terms will be presented to you at the time of purchase or in your billing settings.',
      'Payments are processed by third-party payment providers such as Stripe. By purchasing a paid plan, you authorize the applicable payment provider to charge the payment method you designate in accordance with the pricing and billing terms shown at checkout.',
    ],
  },
  {
    title: 'Your Content',
    paragraphs: [
      'You retain ownership of the prompts, documents, files, slide content, and other materials you submit to Restack. You grant Restack a non-exclusive, worldwide, limited license to host, store, reproduce, transmit, process, and display your content solely as necessary to operate, secure, and improve the service.',
      'You are responsible for ensuring that you have all rights necessary to submit your content and to instruct Restack to process it.',
    ],
  },
  {
    title: 'AI Outputs',
    paragraphs: [
      'Restack uses AI systems to generate and modify content. AI-generated outputs may be incomplete, inaccurate, offensive, or unsuitable for a given purpose. You are responsible for reviewing outputs before relying on them, especially for business, legal, financial, regulatory, or client-facing use.',
      'Restack does not provide legal, accounting, investment, or other professional advice through its outputs.',
    ],
  },
  {
    title: 'Acceptable Use',
    bullets: [
      'Do not use Restack in violation of any law, regulation, contract, or third-party right.',
      'Do not upload or process content you are not authorized to use.',
      'Do not attempt to probe, scan, interfere with, or disrupt the service, its infrastructure, or other users.',
      'Do not reverse engineer, decompile, copy, or misuse the service except as permitted by applicable law.',
      'Do not use the service to generate, distribute, or facilitate malware, fraud, phishing, harassment, discrimination, or unlawful surveillance.',
      'Do not use the service in a way that infringes intellectual property, privacy, confidentiality, or publicity rights.',
    ],
  },
  {
    title: 'Third-Party Services',
    paragraphs: [
      'Restack may rely on third-party services and integrations, including Microsoft, Google, Firebase, Stripe, and AI/model providers. Your use of third-party services may also be governed by those providers\' terms and policies.',
    ],
  },
  {
    title: 'Intellectual Property',
    paragraphs: [
      'Restack and its licensors retain all rights, title, and interest in and to the service, including software, branding, interfaces, and documentation, except for rights expressly granted to you in these Terms.',
    ],
  },
  {
    title: 'Termination',
    paragraphs: [
      'You may stop using Restack at any time. We may suspend or terminate your access if we reasonably believe you have violated these Terms, created risk for other users, exposed Restack to liability, or are using the service in a fraudulent, abusive, or unlawful way.',
    ],
  },
  {
    title: 'Disclaimers',
    paragraphs: [
      'To the maximum extent permitted by law, Restack is provided on an "as is" and "as available" basis, without warranties of any kind, whether express, implied, statutory, or otherwise. We do not warrant that the service will be uninterrupted, error-free, secure, or that outputs will be accurate or fit for a particular purpose.',
    ],
  },
  {
    title: 'Limitation of Liability',
    paragraphs: [
      'To the maximum extent permitted by law, Restack and its affiliates, officers, employees, agents, licensors, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, or business opportunities arising out of or related to your use of the service.',
      'To the maximum extent permitted by law, the total liability of Restack for claims arising out of or related to the service will not exceed the amount you paid to Restack for the service during the 12 months preceding the event giving rise to the claim.',
    ],
  },
  {
    title: 'Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time. If we make material changes, we may revise the effective date above and take additional steps to notify you when appropriate. Your continued use of the service after the updated Terms become effective means you accept them.',
    ],
  },
];

const CONTACT_TITLE = 'Contact Us';

// The on-this-page nav, built from the same list the document renders, so a
// section can never appear in one and not the other.
const SECTION_LINKS = [...sections.map((s) => s.title), CONTACT_TITLE].map((title) => ({
  id: slugify(title),
  label: title,
}));

export default function TermsPage() {
  return (
    <LegalPageLayout
      eyebrow="Terms of Service"
      title="The rules for using Restack"
      subtitle="These terms explain the conditions under which you may use the Restack website, Microsoft Office add-ins, subscriptions, and AI-powered Office tools."
      effectiveDate="March 16, 2026"
      sectionLinks={SECTION_LINKS}
    >
      <SEO
        title="Terms of Service"
        description="The terms governing your use of Restack, the deliverable engine for Private Equity teams, its add-ins, subscriptions, and related services."
        canonical="/terms"
      />
      {sections.map((section) => (
        <React.Fragment key={section.title}>
          <Typography component="h2" id={slugify(section.title)}>{section.title}</Typography>
          {section.paragraphs?.map((paragraph, pIdx) => (
            <Typography key={pIdx}>{paragraph}</Typography>
          ))}
          {section.bullets?.length ? (
            <ul>
              {section.bullets.map((bullet, bIdx) => (
                <li key={bIdx}>{bullet}</li>
              ))}
            </ul>
          ) : null}
        </React.Fragment>
      ))}

      <Typography component="h2" id={slugify(CONTACT_TITLE)}>{CONTACT_TITLE}</Typography>
      <Typography>
        For questions about these Terms, email{' '}
        <Link href="mailto:hi@re-stack.com">hi@re-stack.com</Link>.
      </Typography>
    </LegalPageLayout>
  );
}
